import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ErrorState, LoadingState } from "../../components/states";
import { useToast } from "../../components/toast/toastContext";
import { fetchMcpCommandRetention, updateMcpCommandRetention } from "../../api/mcpCommandRetention";
import { NumberField, SettingForm, SettingSubsection } from "../../components/admin/SettingPanel";

const SettingsWritePermission = "settings.write";

/**
 * How long a finished MCP command's receipt is kept, in hours: the window in which an AI application's
 * retry of the same command returns the original answer instead of repeating it. The server refuses a
 * value outside 1 hour and its configured maximum and says why.
 */
export function McpCommandRetentionSection(): React.JSX.Element {
  const { t } = useTranslation();
  const [state, setState] = useState<{ kind: "loading" } | { kind: "loaded" } | { kind: "error"; message: string }>({ kind: "loading" });
  const [hours, setHours] = useState("");
  const [maximumHours, setMaximumHours] = useState(24);
  const [pending, setPending] = useState(false);
  const { show } = useToast();

  const load = useCallback(async () => {
    setState({ kind: "loading" });
    const result = await fetchMcpCommandRetention();
    if (result.kind === "ok") {
      setHours(String(result.response.hours));
      setMaximumHours(result.response.maximumHours);
      setState({ kind: "loaded" });
    } else if (result.kind === "denied") {
      setState({ kind: "error", message: t("admin.mcpCommandRetention.denied") });
    } else {
      setState({ kind: "error", message: result.message });
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  const save = async () => {
    setPending(true);
    const result = await updateMcpCommandRetention(Number(hours));
    setPending(false);
    if (result.kind === "ok") {
      setHours(String(result.response.hours));
      show(t("admin.mcpCommandRetention.saved"), "success");
    } else if (result.kind === "denied") {
      show(t("admin.denied.description", { permissions: SettingsWritePermission }), "error");
    } else {
      show(result.message, "error");
    }
  };

  if (state.kind === "loading") {
    return <LoadingState label={t("admin.mcpCommandRetention.loading")} />;
  }

  if (state.kind === "error") {
    return <ErrorState description={state.message} onRetry={() => void load()} />;
  }

  return (
    <SettingSubsection id="mcp-command-retention" title={t("admin.mcpCommandRetention.title")} explanation={t("admin.mcpCommandRetention.explanation")}>
      <SettingForm>
        <NumberField id="mcp-command-retention-hours" label={t("admin.mcpCommandRetention.hours")} min={1} max={maximumHours} value={hours} onChange={setHours} />
        <button type="button" className="button button-secondary" disabled={pending} onClick={() => void save()}>
          {t("admin.mcpCommandRetention.save")}
        </button>
      </SettingForm>
    </SettingSubsection>
  );
}
